import React, { useEffect, useState } from "react";
import { InputField } from "@/components/inputFields/inputFields";
import styles from "./css/LoginCard.module.css";
import { LoginData, UserData } from "@/types";
import { Button } from "@/components/Buttons";
import { useLogin } from "@/hooks/gateway/useLogin";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "@/store/userSlice";
import { setUserEntity } from "@/store/entitySlice";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFetchAllEntities,
  useFetchAllUsers,
  useFetchRoles
} from "@/utils/fetchData";
import { setToast } from "@/store/systemSlice";
import { RootState } from "@/store/store";
import { useResetPassword } from "@/hooks/gateway/useResetPassword";
import { useTranslation } from 'react-i18next';
import { langDirection } from "@/utils/languages";
import { ArrowLeftIcon } from "@/icons/ArrowLeftIcon";
import { getEntityIdPair } from "@/utils/getEntityPairId";
import { useFetchAllProjects } from "@/utils/fetchData";

export const LoginCard: React.FC = () => {
  const { t, i18n } = useTranslation('LoginCard');
  const currentLanguage = i18n.language;
  const shouldRotate = langDirection(currentLanguage) === "rtl"
  const { entityName } = useParams<{ entityName: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const direction = langDirection(i18n.language)
  const [forgotPassword, setForgotPassword] = useState(false);
  const [details, setDetails] = useState<LoginData>({
    entityName: entityName as string,
    identity: "",
    password: "",
  });
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [doneAll, setDoneALL] = useState(false);
  const [userData, setUserData] = useState<UserData>();
  const { getAllCities, getAllOffices } = useFetchAllEntities();
  const getAllUsers = useFetchAllUsers();
  const getAllRoles = useFetchRoles();
  const getAllProjects = useFetchAllProjects();

  const resetPassword = useResetPassword({
    onSuccess(data) {
      setLoading(false);
      dispatch(
        setToast({
          toastTitle: data.message,
          toastMessage: "",
          toastType: "success",
        })
      );
    },
    onError(error) {
      setLoading(false);
      dispatch(
        setToast({
          toastTitle: error.response.data.message,
          toastMessage: "",
          toastType: "error",
        })
      );
    },
  })
  const login = useLogin({
    onSuccess(data) {
      setUserData(data.user);
      const user = {
        user: data.user,
        token: data.token,
        refreshToken: data.user.refresh_token,
        userAuth: true,
        users: [],
      };
      const entityType = data.city ? "city" : "ao";
      const body = { ...getEntityIdPair(entityType, data.city, data.ao) };
      
      getAllRoles.mutate({...getEntityIdPair(entityType, data.city, data.ao)});

      if (data.user.user_type === "admin") {
        getAllUsers.mutate(body);
      }

      if (user.user.user_type === "super_admin") {
        getAllCities.mutate({});
        getAllOffices.mutate();
      } else if (
        user.user.user_type === "admin" ||
        user.user.user_type === "internal_municipal" ||
        user.user.user_type === "external_municipal"
      ) {
        getAllProjects.mutate(body);
      }

      if (user.user.user_type === "external_municipal") {
        getAllCities.mutate(body);
      }
      if(data.city){
        dispatch(setUserEntity({userCity: data.city, entityType: "city"}));
      }
      if(data.ao){
        dispatch(setUserEntity({userAo: data.ao, entityType: "ao"}));
      }
      dispatch(setUser(user));
      setDoneALL(true);
    },
    onError(error) {
      dispatch(
        setToast({
          toastTitle: error.response.data.message,
          toastMessage: "",
          toastType: "error",
        })
      );
      setLoading(false);
    },
  });

  useEffect(() => {
    if (userData) {
      if (
        ( userData.user_type !== "") &&
        doneAll
      ) {
        if (userData.user_type in ["internal_municipal", "admin"]) {
            navigate(`/${entityName}/city`);
        } else if (userData.user_type === "external_municipal") {
          navigate(`/${entityName}/cities`);
        }
      }
    }
  }, [userData, doneAll]);

  const handleInputChange = (name: string, value: string) => {
    if (name === "email") {
      setEmail(value);
    } else {
      setDetails((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleClick = () => {
    setLoading(true);
    if(forgotPassword){
      resetPassword.mutate({email})
    }
    else {
      login.mutate(details);
    }
  };

  const handleForgotPassword = () => {
    if (!loading) {
      setForgotPassword(!forgotPassword);
      setEmail("");
      // setDetails({ identity: "", password: "", entityName: entityName as string });
    }
  };

  return (
    <div className={styles.card}>
      <div className={`${styles.topLabel} ${styles[direction]}`}>{t('Login')}</div>
      <div className={`${styles.formContainer}`}>
        <div className={`${styles.form} ${forgotPassword ? styles.swap : ""}`}>
          <div className={`${styles.formContent} ${styles.front}`}>
            <div className={styles.textInputContainer}>
              <label htmlFor="userEmail" className={`${styles.label} ${styles[direction]}`}>
                {t('EmailOrName')}
              </label>
              <div className={styles.inputField}>
                <InputField
                  id="userEmail"
                  placeholder={t('EnterEmailOrName')}
                  inputType="text"
                  data={details.identity}
                  setData={(value: string | undefined) =>
                    handleInputChange("identity", value || '')
                  }
                />
              </div>
            </div>
            <div className={styles.textInputContainerPassword}>
              <label htmlFor="password" className={`${styles.label} ${styles[direction]}`}>
                {t('Password')}
              </label>
              <div className={styles.inputField}>
                <InputField
                  id="password"
                  placeholder={t('EnterPassword')}
                  inputType="password"
                  data={details.password}
                  setData={(value: string | undefined) =>
                    handleInputChange("password", value || '')
                  }
                />
              </div>
            </div>
            <Button
              text={t('Login')}
              icon="no"
              onClick={handleClick}
              size="large"
              className={shouldRotate ? styles.buttonH: styles.buttonE }
              loading={loading}
              disabled={details.password === "" || details.identity === ""}
              id={"login-btn"}
            />
            <div className={styles.bottomLabelContainer}>
              {/* <div
                className={`${styles.bottomLabel} ${styles[direction]}`}
                dir={direction}
                onClick={handleForgotPassword}
                data-testid={"forgot-pass"}
              >
                {t('ForgotPassword')}
              </div> */}
            </div>
          </div>
          <div className={`${styles.formContent} ${styles.back}`}>
          <div className={`${styles.forgotLabel} ${styles[direction]}`}>{t('Forgot')}</div>
            <div className={styles.textInputContainer}>
              <label htmlFor="email" className={`${styles.label} ${styles[direction]}`}>
                {t('Email')}
              </label>
              <div className={styles.inputField}>
                <InputField
                  id="email"
                  placeholder={t('EnterEmail')}
                  inputType="text"
                  data={email}
                  setData={(value: string | undefined) => handleInputChange("email", value || '')}
                />
              </div>
            </div>
            <Button
              text={t('SendLink')}
              icon="no"
              onClick={handleClick}
              size="large"
              className={`${shouldRotate ? styles.forgotPasswordButtonH: styles.forgotPasswordButtonE }`}
              loading={loading}
              disabled={email === ""}
              id={"reset-pass-btn"}
              space={true}
            />
            <div className={styles.bottomLabelContainer}>
              <div
                className={`${styles.bottomLabelLogin} ${styles[direction]}`}
                data-testid={"back-to-login"}
              >
                <Button 
                text={t('BackLogin')}
                icon="left-side"
                iconComponent={ArrowLeftIcon}
                ready={true}
                id={"go-back-to-login"}
                onClick={handleForgotPassword}
                color={"gray"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginCard;
